/**
 * SubsetTool - Archive Subset Management
 *
 * Full workflow for creating, evaluating, building, and exporting
 * archive subsets. Self-contained component (no props from App.tsx).
 *
 * View state machine:
 *   LIST -> CREATE/EDIT -> DETAIL (overview -> evaluating -> evaluated -> building -> built -> exporting -> exported)
 *
 * @module @humanizer/studio/components/tools/SubsetTool
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSubsetApi } from '../../hooks/useSubsetApi';
import { useArchiveApi } from '../../hooks/useArchiveApi';
import { SourceFilterPills } from '../ui/SourceFilterPills';
import type { SourceInfo, FilterState } from '../ui/SourceFilterPills';
import type {
  SubsetData,
  SubsetEvaluateResult,
  SubsetBuildMappingsResult,
  SubsetExportResult,
} from '../../hooks/useSubsetApi';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

type View = 'list' | 'create' | 'edit' | 'detail';
type DetailPhase = 'overview' | 'evaluating' | 'evaluated' | 'building' | 'built' | 'exporting' | 'exported';

interface FormState {
  name: string;
  description: string;
  sourceTypeFilters: FilterState;
  dateFrom: string;
  dateTo: string;
  authorRoles: Set<string>;
  minWordCount: number;
  maxSensitivity: string;
  sensitiveContentAction: string;
}

const AUTHOR_ROLES = ['user', 'assistant', 'system', 'tool'] as const;
const SENSITIVITY_LEVELS = ['public', 'internal', 'private', 'sensitive'] as const;
const SENSITIVE_ACTIONS = [
  { value: 'exclude', label: 'Exclude', description: 'Remove sensitive nodes entirely' },
  { value: 'redact', label: 'Redact', description: 'Replace sensitive content with placeholders' },
  { value: 'include', label: 'Include', description: 'Keep as-is (no filtering)' },
] as const;

const EXPORT_FORMATS = [
  { value: 'json', label: 'JSON', description: 'Structured JSON document' },
  { value: 'jsonl', label: 'JSONL', description: 'One JSON object per line' },
  { value: 'markdown', label: 'Markdown', description: 'Human-readable markdown' },
  { value: 'html', label: 'HTML', description: 'Styled HTML document' },
  { value: 'sqlite', label: 'SQLite', description: 'Portable SQLite database' },
  { value: 'archive', label: 'Archive', description: 'Full archive interchange format' },
] as const;

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  building: 'Building',
  ready: 'Ready',
  exporting: 'Exporting',
  shared: 'Shared',
  error: 'Error',
};

function defaultForm(): FormState {
  return {
    name: '',
    description: '',
    sourceTypeFilters: {},
    dateFrom: '',
    dateTo: '',
    authorRoles: new Set<string>(),
    minWordCount: 0,
    maxSensitivity: 'sensitive',
    sensitiveContentAction: 'exclude',
  };
}

function formToCriteria(form: FormState): Record<string, unknown> {
  const criteria: Record<string, unknown> = {};
  const selectedTypes = Object.entries(form.sourceTypeFilters)
    .filter(([, v]) => v === 'include')
    .map(([k]) => k);
  if (selectedTypes.length > 0) criteria.sourceTypes = selectedTypes;
  if (form.dateFrom) criteria.dateFrom = new Date(form.dateFrom).getTime();
  if (form.dateTo) criteria.dateTo = new Date(form.dateTo + 'T23:59:59').getTime();
  if (form.authorRoles.size > 0) criteria.authorRoles = [...form.authorRoles];
  if (form.minWordCount > 0) criteria.minWordCount = form.minWordCount;
  if (form.maxSensitivity !== 'sensitive') criteria.maxSensitivity = form.maxSensitivity;
  if (form.sensitiveContentAction !== 'exclude') criteria.sensitiveContentAction = form.sensitiveContentAction;
  return criteria;
}

function criteriaToForm(criteria: Record<string, unknown>): Partial<FormState> {
  const patch: Partial<FormState> = {};
  if (Array.isArray(criteria.sourceTypes)) {
    const f: FilterState = {};
    for (const t of criteria.sourceTypes) f[t as string] = 'include';
    patch.sourceTypeFilters = f;
  }
  if (typeof criteria.dateFrom === 'number') {
    patch.dateFrom = new Date(criteria.dateFrom).toISOString().split('T')[0];
  }
  if (typeof criteria.dateTo === 'number') {
    patch.dateTo = new Date(criteria.dateTo).toISOString().split('T')[0];
  }
  if (Array.isArray(criteria.authorRoles)) patch.authorRoles = new Set(criteria.authorRoles as string[]);
  if (typeof criteria.minWordCount === 'number') patch.minWordCount = criteria.minWordCount;
  if (criteria.maxSensitivity) patch.maxSensitivity = criteria.maxSensitivity as string;
  if (criteria.sensitiveContentAction) patch.sensitiveContentAction = criteria.sensitiveContentAction as string;
  return patch;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function SubsetTool(): React.ReactElement {
  const subsetApi = useSubsetApi();
  const archiveApi = useArchiveApi();

  // View state
  const [view, setView] = useState<View>('list');
  const [detailPhase, setDetailPhase] = useState<DetailPhase>('overview');

  // List data
  const [subsets, setSubsets] = useState<SubsetData[]>([]);
  const [listLoading, setListLoading] = useState(false);

  // Sources for criteria builder
  const [sources, setSources] = useState<SourceInfo[]>([]);

  // Current subset (detail/edit)
  const [activeSubset, setActiveSubset] = useState<SubsetData | null>(null);

  // Form state
  const [form, setForm] = useState<FormState>(defaultForm);

  // Operation results
  const [evalResult, setEvalResult] = useState<SubsetEvaluateResult | null>(null);
  const [buildResult, setBuildResult] = useState<SubsetBuildMappingsResult | null>(null);
  const [exportResult, setExportResult] = useState<SubsetExportResult | null>(null);

  // Progress
  const [buildProgress, setBuildProgress] = useState<{ processed: number; total: number } | null>(null);
  const [exportProgress, setExportProgress] = useState<{ exported: number; total: number; stage: string } | null>(null);

  // Export options
  const [exportFormat, setExportFormat] = useState('json');
  const [exportCompress, setExportCompress] = useState(false);
  const [exportIncludeMetadata, setExportIncludeMetadata] = useState(true);

  // Error
  const [error, setError] = useState<string | null>(null);

  // Delete confirm
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Refs for cleanup
  const cleanupRef = useRef<(() => void) | null>(null);

  // ─── Data Loading ────────────────────────────────────────────────────

  const loadSubsets = useCallback(async () => {
    if (!subsetApi.isAvailable) return;
    setListLoading(true);
    setError(null);
    try {
      const result = await subsetApi.list();
      setSubsets(result.subsets);
    } catch (err: any) {
      setError(err.message || 'Failed to load subsets');
    } finally {
      setListLoading(false);
    }
  }, [subsetApi]);

  const loadSources = useCallback(async () => {
    if (!archiveApi.isAvailable) return;
    try {
      const result = await archiveApi.getSources();
      setSources(result.sources.map((s) => ({
        sourceType: s.sourceType,
        count: s.count,
      })));
    } catch {
      // Non-critical — form still works without source list
    }
  }, [archiveApi]);

  // Load on mount
  useEffect(() => {
    loadSubsets();
    loadSources();
  }, [loadSubsets, loadSources]);

  // Cleanup progress listeners on unmount
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  // ─── Navigation ──────────────────────────────────────────────────────

  const goToList = useCallback(() => {
    setView('list');
    setActiveSubset(null);
    setEvalResult(null);
    setBuildResult(null);
    setExportResult(null);
    setBuildProgress(null);
    setExportProgress(null);
    setError(null);
    setDeleteConfirmId(null);
    loadSubsets();
  }, [loadSubsets]);

  const goToCreate = useCallback(() => {
    setForm(defaultForm());
    setView('create');
    setError(null);
  }, []);

  const goToEdit = useCallback((subset: SubsetData) => {
    setActiveSubset(subset);
    setForm({
      ...defaultForm(),
      name: subset.name,
      description: subset.description || '',
      ...criteriaToForm(subset.criteria),
    });
    setView('edit');
    setError(null);
  }, []);

  const goToDetail = useCallback((subset: SubsetData) => {
    setActiveSubset(subset);
    setDetailPhase('overview');
    setEvalResult(null);
    setBuildResult(null);
    setExportResult(null);
    setBuildProgress(null);
    setExportProgress(null);
    setView('detail');
    setError(null);
  }, []);

  // ─── CRUD Operations ────────────────────────────────────────────────

  const handleCreate = useCallback(async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setError(null);
    try {
      const subset = await subsetApi.create({
        name: form.name.trim(),
        criteria: formToCriteria(form),
        description: form.description.trim() || undefined,
      });
      goToDetail(subset);
    } catch (err: any) {
      setError(err.message || 'Failed to create subset');
    }
  }, [form, subsetApi, goToDetail]);

  const handleUpdate = useCallback(async () => {
    if (!activeSubset || !form.name.trim()) {
      setError('Name is required');
      return;
    }
    setError(null);
    try {
      const updated = await subsetApi.update(activeSubset.id, {
        name: form.name.trim(),
        criteria: formToCriteria(form),
        description: form.description.trim() || undefined,
      });
      if (updated) goToDetail(updated);
    } catch (err: any) {
      setError(err.message || 'Failed to update subset');
    }
  }, [activeSubset, form, subsetApi, goToDetail]);

  const handleDelete = useCallback(async (subsetId: string) => {
    setError(null);
    try {
      await subsetApi.delete(subsetId);
      setDeleteConfirmId(null);
      if (activeSubset?.id === subsetId) {
        goToList();
      } else {
        loadSubsets();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete subset');
    }
  }, [subsetApi, activeSubset, goToList, loadSubsets]);

  // ─── Operations ──────────────────────────────────────────────────────

  const handleEvaluate = useCallback(async () => {
    if (!activeSubset) return;
    setDetailPhase('evaluating');
    setError(null);
    try {
      const result = await subsetApi.evaluate(activeSubset.id);
      setEvalResult(result);
      setDetailPhase('evaluated');
    } catch (err: any) {
      setError(err.message || 'Evaluation failed');
      setDetailPhase('overview');
    }
  }, [activeSubset, subsetApi]);

  const handleBuild = useCallback(async () => {
    if (!activeSubset) return;
    setDetailPhase('building');
    setError(null);
    const cleanup = subsetApi.onBuildMappingsProgress(setBuildProgress);
    cleanupRef.current = cleanup;
    try {
      const result = await subsetApi.buildMappings(activeSubset.id);
      setBuildResult(result);
      setDetailPhase('built');
    } catch (err: any) {
      setError(err.message || 'Build failed');
      setDetailPhase('evaluated');
    } finally {
      cleanup();
      cleanupRef.current = null;
      setBuildProgress(null);
    }
  }, [activeSubset, subsetApi]);

  const handleExport = useCallback(async () => {
    if (!activeSubset) return;
    setDetailPhase('exporting');
    setError(null);
    const cleanup = subsetApi.onExportProgress(setExportProgress);
    cleanupRef.current = cleanup;
    try {
      const result = await subsetApi.exportSubset(activeSubset.id, {
        format: exportFormat,
        compress: exportCompress,
        includeMetadata: exportIncludeMetadata,
      });
      setExportResult(result);
      setDetailPhase('exported');
    } catch (err: any) {
      setError(err.message || 'Export failed');
      setDetailPhase('built');
    } finally {
      cleanup();
      cleanupRef.current = null;
      setExportProgress(null);
    }
  }, [activeSubset, subsetApi, exportFormat, exportCompress, exportIncludeMetadata]);

  // ─── Form Helpers ────────────────────────────────────────────────────

  const updateForm = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleRole = useCallback((role: string) => {
    setForm((prev) => {
      const next = new Set(prev.authorRoles);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return { ...prev, authorRoles: next };
    });
  }, []);

  // ─── Criteria Summary ────────────────────────────────────────────────

  const criteriaSummary = useMemo(() => {
    if (!activeSubset) return [];
    const c = activeSubset.criteria;
    const lines: string[] = [];
    if (Array.isArray(c.sourceTypes) && c.sourceTypes.length > 0) {
      lines.push(`Sources: ${(c.sourceTypes as string[]).join(', ')}`);
    }
    if (c.dateFrom || c.dateTo) {
      lines.push(`Date: ${c.dateFrom || '...'} to ${c.dateTo || '...'}`);
    }
    if (Array.isArray(c.authorRoles) && c.authorRoles.length > 0) {
      lines.push(`Roles: ${(c.authorRoles as string[]).join(', ')}`);
    }
    if (typeof c.minWordCount === 'number' && c.minWordCount > 0) {
      lines.push(`Min words: ${c.minWordCount}`);
    }
    if (c.maxSensitivity && c.maxSensitivity !== 'sensitive') {
      lines.push(`Max sensitivity: ${c.maxSensitivity}`);
    }
    if (c.sensitiveContentAction && c.sensitiveContentAction !== 'exclude') {
      lines.push(`Sensitive content: ${c.sensitiveContentAction}`);
    }
    if (lines.length === 0) lines.push('No filters (all archive content)');
    return lines;
  }, [activeSubset]);

  // ─── Unavailable State ───────────────────────────────────────────────

  if (!subsetApi.isAvailable) {
    return (
      <div className="subset-tool">
        <div className="subset-tool__empty">
          <span className="subset-tool__empty-icon">📦</span>
          <p className="subset-tool__empty-text">
            Archive subsets require the desktop app with a connected archive.
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════════════════════════════════

  if (view === 'list') {
    return (
      <div className="subset-tool">
        <div className="subset-tool__header">
          <h3 className="subset-tool__title">Archive Subsets</h3>
          <button className="subset-tool__btn subset-tool__btn--primary" onClick={goToCreate}>
            + New Subset
          </button>
        </div>

        {error && <div className="subset-tool__error">{error}</div>}

        {listLoading ? (
          <div className="subset-tool__loading">Loading subsets...</div>
        ) : subsets.length === 0 ? (
          <div className="subset-tool__empty">
            <span className="subset-tool__empty-icon">📦</span>
            <p className="subset-tool__empty-text">
              No subsets yet. Create one to curate and export a portion of your archive.
            </p>
            <button className="subset-tool__btn subset-tool__btn--primary" onClick={goToCreate}>
              Create First Subset
            </button>
          </div>
        ) : (
          <div className="subset-tool__list">
            {subsets.map((subset) => (
              <div key={subset.id} className="subset-tool__card" onClick={() => goToDetail(subset)}>
                <div className="subset-tool__card-header">
                  <span className="subset-tool__card-name">{subset.name}</span>
                  <span className={`subset-tool__status-badge subset-tool__status-badge--${subset.status}`}>
                    {STATUS_LABELS[subset.status] || subset.status}
                  </span>
                </div>
                <div className="subset-tool__card-meta">
                  {subset.nodeCount != null && (
                    <span>{subset.nodeCount.toLocaleString()} nodes</span>
                  )}
                  {subset.totalWordCount != null && (
                    <span>{subset.totalWordCount.toLocaleString()} words</span>
                  )}
                  <span>{formatDate(subset.createdAt)}</span>
                </div>
                <div className="subset-tool__card-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="subset-tool__btn subset-tool__btn--small"
                    onClick={() => goToDetail(subset)}
                  >
                    View
                  </button>
                  <button
                    className="subset-tool__btn subset-tool__btn--small"
                    onClick={() => goToEdit(subset)}
                  >
                    Edit
                  </button>
                  {deleteConfirmId === subset.id ? (
                    <>
                      <button
                        className="subset-tool__btn subset-tool__btn--danger subset-tool__btn--small"
                        onClick={() => handleDelete(subset.id)}
                      >
                        Confirm
                      </button>
                      <button
                        className="subset-tool__btn subset-tool__btn--small"
                        onClick={() => setDeleteConfirmId(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="subset-tool__btn subset-tool__btn--danger subset-tool__btn--small"
                      onClick={() => setDeleteConfirmId(subset.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // CREATE / EDIT FORM
  // ═══════════════════════════════════════════════════════════════════════

  if (view === 'create' || view === 'edit') {
    return (
      <div className="subset-tool">
        <div className="subset-tool__header">
          <button className="subset-tool__btn subset-tool__btn--small" onClick={goToList}>
            &larr; Back
          </button>
          <h3 className="subset-tool__title">
            {view === 'create' ? 'New Subset' : 'Edit Subset'}
          </h3>
        </div>

        {error && <div className="subset-tool__error">{error}</div>}

        <div className="subset-tool__form">
          {/* Name */}
          <div className="subset-tool__form-section">
            <label className="subset-tool__form-label">Name</label>
            <input
              className="subset-tool__form-input"
              type="text"
              value={form.name}
              onChange={(e) => updateForm('name', e.target.value)}
              placeholder="My subset"
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="subset-tool__form-section">
            <label className="subset-tool__form-label">Description</label>
            <input
              className="subset-tool__form-input"
              type="text"
              value={form.description}
              onChange={(e) => updateForm('description', e.target.value)}
              placeholder="Optional description"
            />
          </div>

          {/* Source Types */}
          {sources.length > 0 && (
            <div className="subset-tool__form-section">
              <label className="subset-tool__form-label">Source Types</label>
              <SourceFilterPills
                sources={sources}
                filters={form.sourceTypeFilters}
                onFiltersChange={(f) => updateForm('sourceTypeFilters', f)}
                mode="toggle"
                showCounts
                showAll
              />
            </div>
          )}

          {/* Date Range */}
          <div className="subset-tool__form-section">
            <label className="subset-tool__form-label">Date Range</label>
            <div className="subset-tool__form-row">
              <input
                className="subset-tool__form-input"
                type="date"
                value={form.dateFrom}
                onChange={(e) => updateForm('dateFrom', e.target.value)}
              />
              <span className="subset-tool__form-separator">to</span>
              <input
                className="subset-tool__form-input"
                type="date"
                value={form.dateTo}
                onChange={(e) => updateForm('dateTo', e.target.value)}
              />
            </div>
          </div>

          {/* Author Roles */}
          <div className="subset-tool__form-section">
            <label className="subset-tool__form-label">Author Roles</label>
            <div className="subset-tool__checkbox-group">
              {AUTHOR_ROLES.map((role) => (
                <label key={role} className="subset-tool__checkbox-item">
                  <input
                    type="checkbox"
                    checked={form.authorRoles.has(role)}
                    onChange={() => toggleRole(role)}
                  />
                  <span>{role}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Min Word Count */}
          <div className="subset-tool__form-section">
            <label className="subset-tool__form-label">Min Word Count</label>
            <input
              className="subset-tool__form-input subset-tool__form-input--narrow"
              type="number"
              min={0}
              value={form.minWordCount || ''}
              onChange={(e) => updateForm('minWordCount', parseInt(e.target.value, 10) || 0)}
              placeholder="0"
            />
          </div>

          {/* Sensitivity Level */}
          <div className="subset-tool__form-section">
            <label className="subset-tool__form-label">Max Sensitivity</label>
            <div className="subset-tool__radio-group">
              {SENSITIVITY_LEVELS.map((level) => (
                <label key={level} className="subset-tool__checkbox-item">
                  <input
                    type="radio"
                    name="sensitivity"
                    checked={form.maxSensitivity === level}
                    onChange={() => updateForm('maxSensitivity', level)}
                  />
                  <span>{level}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Sensitive Content Action */}
          <div className="subset-tool__form-section">
            <label className="subset-tool__form-label">Sensitive Content</label>
            <div className="subset-tool__radio-group">
              {SENSITIVE_ACTIONS.map((opt) => (
                <label key={opt.value} className="subset-tool__checkbox-item">
                  <input
                    type="radio"
                    name="sensitiveAction"
                    checked={form.sensitiveContentAction === opt.value}
                    onChange={() => updateForm('sensitiveContentAction', opt.value)}
                  />
                  <span title={opt.description}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="subset-tool__form-footer">
            <button className="subset-tool__btn" onClick={goToList}>
              Cancel
            </button>
            <button
              className="subset-tool__btn subset-tool__btn--primary"
              onClick={view === 'create' ? handleCreate : handleUpdate}
            >
              {view === 'create' ? 'Create Subset' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ═══════════════════════════════════════════════════════════════════════

  if (view === 'detail' && activeSubset) {
    return (
      <div className="subset-tool">
        <div className="subset-tool__header">
          <button className="subset-tool__btn subset-tool__btn--small" onClick={goToList}>
            &larr; Back
          </button>
          <h3 className="subset-tool__title">{activeSubset.name}</h3>
          <span className={`subset-tool__status-badge subset-tool__status-badge--${activeSubset.status}`}>
            {STATUS_LABELS[activeSubset.status] || activeSubset.status}
          </span>
        </div>

        {error && <div className="subset-tool__error">{error}</div>}

        <div className="subset-tool__detail">
          {/* Criteria Summary */}
          <div className="subset-tool__criteria-summary">
            <h4 className="subset-tool__section-title">Criteria</h4>
            {criteriaSummary.map((line, i) => (
              <div key={i} className="subset-tool__criteria-line">{line}</div>
            ))}
            <button
              className="subset-tool__btn subset-tool__btn--small"
              onClick={() => goToEdit(activeSubset)}
            >
              Edit Criteria
            </button>
          </div>

          {/* ── Stats (after evaluate) ── */}
          {evalResult && (
            <div className="subset-tool__stats">
              <h4 className="subset-tool__section-title">Evaluation</h4>
              <div className="subset-tool__stat-row">
                <span>Nodes</span>
                <strong>{evalResult.nodeCount.toLocaleString()}</strong>
              </div>
              <div className="subset-tool__stat-row">
                <span>Words</span>
                <strong>{evalResult.totalWordCount.toLocaleString()}</strong>
              </div>
              {evalResult.dateRange.earliest && evalResult.dateRange.latest && (
                <div className="subset-tool__stat-row">
                  <span>Date Range</span>
                  <strong>
                    {formatDate(evalResult.dateRange.earliest)} &ndash; {formatDate(evalResult.dateRange.latest)}
                  </strong>
                </div>
              )}
              {evalResult.redactableNodes > 0 && (
                <div className="subset-tool__stat-row">
                  <span>Redactable</span>
                  <strong>{evalResult.redactableNodes.toLocaleString()}</strong>
                </div>
              )}

              {/* Source distribution */}
              {Object.keys(evalResult.sourceDistribution).length > 0 && (
                <div className="subset-tool__distribution">
                  <h5 className="subset-tool__distribution-title">Sources</h5>
                  <DistributionBar
                    data={evalResult.sourceDistribution}
                    total={evalResult.nodeCount}
                  />
                </div>
              )}

              {/* Sensitivity distribution */}
              {Object.keys(evalResult.sensitivityDistribution).length > 0 && (
                <div className="subset-tool__distribution">
                  <h5 className="subset-tool__distribution-title">Sensitivity</h5>
                  <DistributionBar
                    data={evalResult.sensitivityDistribution}
                    total={evalResult.nodeCount}
                    colorMap={SENSITIVITY_COLORS}
                  />
                </div>
              )}
            </div>
          )}

          {/* ── Build Result ── */}
          {buildResult && (
            <div className="subset-tool__stats">
              <h4 className="subset-tool__section-title">Mappings</h4>
              <div className="subset-tool__stat-row">
                <span>Created</span>
                <strong>{buildResult.created.toLocaleString()}</strong>
              </div>
              <div className="subset-tool__stat-row">
                <span>Skipped</span>
                <strong>{buildResult.skipped.toLocaleString()}</strong>
              </div>
              <div className="subset-tool__stat-row">
                <span>Total</span>
                <strong>{buildResult.total.toLocaleString()}</strong>
              </div>
            </div>
          )}

          {/* ── Export Result ── */}
          {exportResult && (
            <div className="subset-tool__export-result">
              <h4 className="subset-tool__section-title">Export Complete</h4>
              <div className="subset-tool__stat-row">
                <span>Format</span>
                <strong>{exportResult.format}</strong>
              </div>
              <div className="subset-tool__stat-row">
                <span>Size</span>
                <strong>{formatBytes(exportResult.sizeBytes)}</strong>
              </div>
              <div className="subset-tool__stat-row">
                <span>Duration</span>
                <strong>{formatDuration(exportResult.durationMs)}</strong>
              </div>
              <div className="subset-tool__stat-row">
                <span>Exported</span>
                <strong>{exportResult.nodesExported.toLocaleString()}</strong>
              </div>
              {exportResult.nodesRedacted > 0 && (
                <div className="subset-tool__stat-row">
                  <span>Redacted</span>
                  <strong>{exportResult.nodesRedacted.toLocaleString()}</strong>
                </div>
              )}
              {exportResult.mediaFilesIncluded != null && exportResult.mediaFilesIncluded > 0 && (
                <div className="subset-tool__stat-row">
                  <span>Media Files</span>
                  <strong>{exportResult.mediaFilesIncluded.toLocaleString()}</strong>
                </div>
              )}
              {exportResult.mediaFilesMissing != null && exportResult.mediaFilesMissing > 0 && (
                <div className="subset-tool__stat-row">
                  <span>Media Missing</span>
                  <strong>{exportResult.mediaFilesMissing.toLocaleString()}</strong>
                </div>
              )}
              {exportResult.mediaSizeBytes != null && exportResult.mediaSizeBytes > 0 && (
                <div className="subset-tool__stat-row">
                  <span>Media Size</span>
                  <strong>{formatBytes(exportResult.mediaSizeBytes)}</strong>
                </div>
              )}
              <div className="subset-tool__stat-row subset-tool__stat-row--path">
                <span>Path</span>
                <code className="subset-tool__path">{exportResult.destination}</code>
              </div>
            </div>
          )}

          {/* ── Progress Bars ── */}
          {detailPhase === 'evaluating' && (
            <div className="subset-tool__progress">
              <div className="subset-tool__progress-text">Evaluating subset...</div>
              <div className="subset-tool__progress-bar">
                <div className="subset-tool__progress-fill subset-tool__progress-fill--indeterminate" />
              </div>
            </div>
          )}

          {detailPhase === 'building' && (
            <div className="subset-tool__progress">
              <div className="subset-tool__progress-text">
                Building mappings{buildProgress ? ` (${buildProgress.processed}/${buildProgress.total})` : '...'}
              </div>
              <div className="subset-tool__progress-bar">
                <div
                  className="subset-tool__progress-fill"
                  style={{ width: buildProgress ? `${Math.round((buildProgress.processed / buildProgress.total) * 100)}%` : '0%' }}
                />
              </div>
            </div>
          )}

          {detailPhase === 'exporting' && (
            <div className="subset-tool__progress">
              <div className="subset-tool__progress-text">
                {exportProgress ? `${exportProgress.stage} (${exportProgress.exported}/${exportProgress.total})` : 'Starting export...'}
              </div>
              <div className="subset-tool__progress-bar">
                <div
                  className="subset-tool__progress-fill"
                  style={{ width: exportProgress ? `${Math.round((exportProgress.exported / exportProgress.total) * 100)}%` : '0%' }}
                />
              </div>
            </div>
          )}

          {/* ── Actions ── */}
          <div className="subset-tool__actions">
            {(detailPhase === 'overview') && (
              <button
                className="subset-tool__btn subset-tool__btn--primary"
                onClick={handleEvaluate}
              >
                Evaluate
              </button>
            )}

            {detailPhase === 'evaluated' && (
              <>
                <button
                  className="subset-tool__btn subset-tool__btn--primary"
                  onClick={handleBuild}
                >
                  Build Mappings
                </button>
                <button
                  className="subset-tool__btn"
                  onClick={handleEvaluate}
                >
                  Re-evaluate
                </button>
              </>
            )}

            {(detailPhase === 'built' || detailPhase === 'exported') && (
              <>
                {/* Export panel */}
                <div className="subset-tool__export">
                  <h4 className="subset-tool__section-title">Export</h4>
                  <div className="subset-tool__format-picker">
                    {EXPORT_FORMATS.map((fmt) => (
                      <button
                        key={fmt.value}
                        className={`subset-tool__format-option ${exportFormat === fmt.value ? 'subset-tool__format-option--selected' : ''}`}
                        onClick={() => setExportFormat(fmt.value)}
                        title={fmt.description}
                      >
                        {fmt.label}
                      </button>
                    ))}
                  </div>
                  <div className="subset-tool__export-options">
                    <label className="subset-tool__checkbox-item">
                      <input
                        type="checkbox"
                        checked={exportFormat === 'archive' ? false : exportCompress}
                        onChange={(e) => setExportCompress(e.target.checked)}
                        disabled={exportFormat === 'archive'}
                      />
                      <span title={exportFormat === 'archive' ? 'Archive format uses ZIP compression' : undefined}>
                        Compress{exportFormat === 'archive' ? ' (ZIP)' : ''}
                      </span>
                    </label>
                    <label className="subset-tool__checkbox-item">
                      <input
                        type="checkbox"
                        checked={exportIncludeMetadata}
                        onChange={(e) => setExportIncludeMetadata(e.target.checked)}
                      />
                      <span>Include Metadata</span>
                    </label>
                  </div>
                  <button
                    className="subset-tool__btn subset-tool__btn--primary"
                    onClick={handleExport}
                  >
                    Start Export
                  </button>
                </div>
                <button
                  className="subset-tool__btn"
                  onClick={handleBuild}
                >
                  Rebuild Mappings
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return <div className="subset-tool" />;
}

// ═══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

const SENSITIVITY_COLORS: Record<string, string> = {
  public: 'var(--color-success)',
  internal: 'var(--color-info)',
  private: 'var(--color-warning)',
  sensitive: 'var(--color-error)',
};

/** Horizontal stacked bar for distribution data */
function DistributionBar({
  data,
  total,
  colorMap,
}: {
  data: Record<string, number>;
  total: number;
  colorMap?: Record<string, string>;
}): React.ReactElement {
  const entries = Object.entries(data).sort(([, a], [, b]) => b - a);
  return (
    <div>
      <div className="subset-tool__distribution-bar">
        {entries.map(([key, count]) => {
          const pct = total > 0 ? (count / total) * 100 : 0;
          if (pct < 0.5) return null;
          return (
            <div
              key={key}
              className="subset-tool__distribution-segment"
              style={{
                width: `${pct}%`,
                backgroundColor: colorMap?.[key] ?? undefined,
              }}
              title={`${key}: ${count.toLocaleString()} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div className="subset-tool__distribution-legend">
        {entries.map(([key, count]) => (
          <span key={key} className="subset-tool__legend-item">
            {colorMap?.[key] && (
              <span
                className="subset-tool__legend-dot"
                style={{ backgroundColor: colorMap[key] }}
              />
            )}
            {key}: {count.toLocaleString()}
          </span>
        ))}
      </div>
    </div>
  );
}

export default SubsetTool;
